// Cart page behaviour:
//   - Quantity − / + per line, and remove
//   - After each mutation, re-render the cart via the Section Rendering API
//     and swap [data-cart-contents] — so Liquid stays the single source of
//     truth for line totals, subtotal, item count and the empty state
//     (no JS money-formatting, no full page reload).
//   - Keep the line dimmed + per-button spinner while the request is in
//     flight (feedback before the swap lands).
//   - Stay in sync when items are added elsewhere on the page (the upsell
//     rail's quick-add) via the global `cart:updated` event.
//
// All Shopify network calls go through window.toyCart (cart-actions.js).

// The cart section id (e.g. "main") is emitted by Liquid as a data attribute
// so it's never hardcoded here.
function getSectionId() {
  const contents = document.querySelector('[data-cart-contents]');
  return contents ? contents.dataset.sectionId : null;
}

// Swap + line-busy logic is shared with the cart drawer via window.toyCart.
function renderCartContents(html) {
  return window.toyCart.renderSection('[data-cart-contents]', html);
}

function setLineBusy(lineEl, busy) {
  window.toyCart.setLineBusy(lineEl, busy);
}

// Slide a removed line out before the swap, for a smoother feel.
function fadeOutLine(lineEl) {
  return new Promise((resolve) => {
    const anim = lineEl.animate(
      [
        { opacity: 1, transform: 'translateX(0)' },
        { opacity: 0, transform: 'translateX(-24px)' },
      ],
      { duration: 200, easing: 'cubic-bezier(0.4, 0, 1, 1)', fill: 'forwards' },
    );
    anim.onfinish = resolve;
    anim.oncancel = resolve;
  });
}

async function handleAction(btn) {
  if (!window.toyCart) return;
  const lineEl = btn.closest('[data-line-key]');
  if (!lineEl) return;

  const key = lineEl.dataset.lineKey;
  if (!key) return;

  const action = btn.dataset.cartAction;
  const qtyEl = lineEl.querySelector('[data-line-quantity]');
  const current = qtyEl ? parseInt(qtyEl.textContent, 10) || 1 : 1;

  let nextQty;
  if (action === 'increment') nextQty = current + 1;
  else if (action === 'decrement') nextQty = current - 1;
  else if (action === 'remove') nextQty = 0;
  else return;
  if (nextQty < 0) return;

  // Per-button spinner + whole-line dim while the request is in flight.
  window.toyCart.setBusy(btn, '');
  setLineBusy(lineEl, true);

  try {
    const sectionId = getSectionId();
    const cart = await window.toyCart.changeCartLine(key, nextQty, sectionId);
    const html = sectionId && cart.sections ? cart.sections[sectionId] : null;

    if (nextQty === 0) {
      await fadeOutLine(lineEl);
    }

    // Swap in the re-rendered section. Fall back to a reload only if the
    // Section Rendering payload is somehow missing (keeps the page correct).
    if (!renderCartContents(html)) {
      window.location.reload();
    }
  } catch (error) {
    console.error('[cart-page] line change failed:', error);
    window.toyCart.setError(btn, error.message);
    setLineBusy(lineEl, false);
  }
}

// ===== Discount codes (Cart Ajax API /cart/update.js) =====
async function applyDiscount(applyBtn) {
  if (!window.toyCart) return;
  const wrap = applyBtn.closest('[data-cart-promo]');
  const input = wrap && wrap.querySelector('[data-discount-input]');
  const errorEl = wrap && wrap.querySelector('[data-discount-error]');
  const code = input ? input.value.trim() : '';
  if (!code) return;
  if (errorEl) errorEl.hidden = true;

  window.toyCart.setBusy(applyBtn, '');
  try {
    const sectionId = getSectionId();
    const cart = await window.toyCart.updateCart({ discount: code }, sectionId);

    // Detect a rejected code via the cart's discount_codes (Ajax cart field).
    const codes = cart.discount_codes || [];
    const match = codes.find((c) => c.code && c.code.toLowerCase() === code.toLowerCase());
    if (match && match.applicable === false) {
      window.toyCart.restore(applyBtn);
      if (errorEl) {
        errorEl.textContent = errorEl.dataset.errorText || 'That code is not valid.';
        errorEl.hidden = false;
      }
      return;
    }

    const html = sectionId && cart.sections ? cart.sections[sectionId] : null;
    if (!renderCartContents(html)) window.location.reload();
  } catch (error) {
    console.error('[cart-page] discount apply failed:', error);
    window.toyCart.restore(applyBtn);
    if (errorEl) {
      errorEl.textContent = error.message;
      errorEl.hidden = false;
    }
  }
}

async function clearDiscount(clearBtn) {
  if (!window.toyCart) return;
  window.toyCart.setBusy(clearBtn, '');
  try {
    const sectionId = getSectionId();
    const cart = await window.toyCart.updateCart({ discount: '' }, sectionId);
    const html = sectionId && cart.sections ? cart.sections[sectionId] : null;
    if (!renderCartContents(html)) window.location.reload();
  } catch (error) {
    console.error('[cart-page] discount clear failed:', error);
    window.toyCart.restore(clearBtn);
  }
}

// ===== Order note (saved to cart.note; no section swap, so focus stays put) =====
let noteTimer;
function saveNote(textarea) {
  if (!window.toyCart) return;
  clearTimeout(noteTimer);
  window.toyCart.updateCart({ note: textarea.value }).catch((error) => {
    console.warn('[cart-page] note save failed:', error);
  });
}

// Re-fetch + swap the cart contents from scratch (Section Rendering GET).
// Used when an item is added from elsewhere on the page (upsell quick-add).
async function refreshCartContents() {
  const sectionId = getSectionId();
  if (!sectionId) return;
  try {
    const url = `${window.location.pathname}?sections=${encodeURIComponent(sectionId)}`;
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!res.ok) return;
    const data = await res.json();
    renderCartContents(data[sectionId]);
  } catch (error) {
    console.warn('[cart-page] refresh failed:', error);
  }
}

function initCartPage() {
  const page = document.querySelector('[data-cart-page]');
  if (!page) return;
  if (!window.toyCart) {
    console.warn('[cart-page] cart-actions not loaded; line actions will not work');
    return;
  }

  // Event delegation on the section (which is never swapped) so listeners
  // survive every [data-cart-contents] innerHTML replacement.
  page.addEventListener('click', (event) => {
    const lineBtn = event.target.closest('[data-cart-action]');
    if (lineBtn && page.contains(lineBtn) && !lineBtn.disabled) {
      event.preventDefault();
      handleAction(lineBtn);
      return;
    }
    const applyBtn = event.target.closest('[data-discount-apply]');
    if (applyBtn && !applyBtn.disabled) {
      event.preventDefault();
      applyDiscount(applyBtn);
      return;
    }
    const clearBtn = event.target.closest('[data-discount-clear]');
    if (clearBtn && !clearBtn.disabled) {
      event.preventDefault();
      clearDiscount(clearBtn);
    }
  });

  // Enter inside the discount input applies it (the input isn't in a form).
  page.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter') return;
    const input = event.target.closest('[data-discount-input]');
    if (!input) return;
    event.preventDefault();
    const wrap = input.closest('[data-cart-promo]');
    const applyBtn = wrap && wrap.querySelector('[data-discount-apply]');
    if (applyBtn) applyDiscount(applyBtn);
  });

  // Order note: save on blur (change) and debounced while typing, so it
  // persists even if a qty change re-renders the section.
  page.addEventListener('change', (event) => {
    const note = event.target.closest('[data-cart-note]');
    if (note) saveNote(note);
  });
  page.addEventListener('input', (event) => {
    const note = event.target.closest('[data-cart-note]');
    if (!note) return;
    clearTimeout(noteTimer);
    noteTimer = setTimeout(() => saveNote(note), 700);
  });

  // Quick-adding from the upsell rail adds an item without touching the cart
  // section — re-render so the line list, totals and count stay accurate.
  document.addEventListener('cart:updated', refreshCartContents);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCartPage);
} else {
  initCartPage();
}
